import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FiEdit, FiArrowLeft, FiTrash } from "react-icons/fi";
import {
  useTable,
  useGlobalFilter,
  useResizeColumns,
  useSortBy,
  usePagination,
} from "react-table";
import AxiosInstance from "../Home/AxiosInstance";
import "./CompanyProfile.css";
import "./employerPortal.css";
import CompanyHeader from "./CompanyHeader";
import Footer from "../Home/Footer";

export default function CompanyProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const isInstitution = location.pathname.includes("institution");
  const { company_id } = useParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [editingAddress, setEditingAddress] = useState(null);
  const [editedAddress, setEditedAddress] = useState(null);
  const [editAddressSearch, setEditAddressSearch] = useState("");
  const [editSuggestions, setEditSuggestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [searchUserTerm, setSearchUserTerm] = useState("");
  const [editedUser, setEditedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [company, setCompany] = useState({
    address: [],
  });
  const [addressTypeOptions, setAddressTypeOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [addressFilter, setAddressFilter] = useState("");

  useEffect(() => {
    if (company_id) {
      localStorage.setItem("company_id", company_id);
    } else {
      console.error("Company ID not found in URL");
    }
  }, [company_id]);

  // Fetch company addresses using company_id from URL params
  useEffect(() => {
    if (!company_id) {
      console.error("Company ID not found in URL");
      return;
    }
    const fetchCompanyAddresses = async () => {
      try {
        const response = await AxiosInstance.get(
          `companies/company/addresses/${company_id}/`
        );

        // Ensure that the response data is an array
        if (Array.isArray(response.data)) {
          setCompany((prevState) => ({
            ...prevState,
            address: response.data,
          }));
        } else {
          console.error(
            "Expected an array of addresses, but got:",
            response.data
          );
          setCompany((prevState) => ({
            ...prevState,
            address: [],
          }));
        }
      } catch (error) {
        console.error("Error fetching company addresses:", error);
      }
    };

    fetchCompanyAddresses();
  }, [company_id]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await AxiosInstance.get(
          "companies/countries/"
        );
        setCountryOptions(response.data);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (editedAddress && editedAddress.country_id) {
      const fetchStates = async () => {
        try {
          const response = await AxiosInstance.get(
            `companies/states/${editedAddress.country_id}/`
          );
          setStateOptions(response.data);
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      };
      fetchStates();
    } else {
      setStateOptions([]); 
    }
  }, [editedAddress?.country_id]); 

  useEffect(() => {
    const fetchAddressTypes = async () => {
      try {
        const response = await AxiosInstance.get(
          "companies/address/types/"
        );
        if (response.status === 200) {
          setAddressTypeOptions(response.data); 
        } else {
          console.error("Failed to fetch address types");
        }
      } catch (error) {
        console.error("Error fetching address types:", error);
      }
    };

    fetchAddressTypes();
  }, []);

  const addressColumns = useMemo(
    () => [
      { Header: "Address Line 1", accessor: "address_line1", resizable: true },
      { Header: "Address Line 2", accessor: "address_line2", resizable: true },
      { Header: "City", accessor: "city", resizable: true },
      { Header: "State", accessor: "state.state_name", resizable: true },
      { Header: "Country", accessor: "country.country_name", resizable: true },
      { Header: "Zip Code", accessor: "zip_code", resizable: true },
      {
        Header: "Address Type",
        accessor: "address_type.address_type",
        resizable: true,
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleEditAddress(row.original)}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleRemoveAddress(row.original.address_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    [company.address]
  );

  const addressTableInstance = useTable(
    {
      columns: addressColumns,
      data: Array.isArray(company.address) ? company.address : [], 
      initialState: { globalFilter: addressFilter, pageSize: 5 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useResizeColumns
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state,
    setGlobalFilter,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    gotoPage,
    pageCount,
    setPageSize,
  } = addressTableInstance;

  const { globalFilter } = state;

  useEffect(() => {
    if (!editAddressSearch) {
      setEditSuggestions([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const res = await AxiosInstance.get(
          "companies/get-address",
          {
            params: { address: editAddressSearch },
          }
        );
        setEditSuggestions(res.data.suggestions || []);
      } catch (err) {
        console.error("Edit autocomplete failed", err);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [editAddressSearch]);

  const handleEditAddressSelect = async (placeId) => {
    setEditSuggestions([]);
    setEditAddressSearch("");

    try {
      const res = await AxiosInstance.get(
        "companies/get-address-details",
        {
          params: { placeId },
        }
      );

      const data = res.data;

      const matchedCountry = countryOptions.find(
        (c) => c.country_name === data.country
      );
      const matchedState = stateOptions.find(
        (s) => s.state_name === data.state
      );

      setEditedAddress((prev) => ({
        ...prev,
        address_line1: data.addressLine1 || "",
        address_line2: data.addressLine2 || "",
        city: data.city || "",
        zip_code: data.postalCode || "",
        country: matchedCountry ? matchedCountry.country_id : "",
        state: matchedState ? matchedState.state_id : "",
      }));
    } catch (err) {
      console.error("Error fetching address details", err);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setAddressFilter(e.target.value);
    setGlobalFilter(e.target.value);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    // Extract country_id and state_id from nested objects
    const countryId = address.country?.country_id || address.country_id || "";
    const stateId = address.state?.state_id || address.state_id || "";
    const addressTypeId = address.address_type?.address_type_id || address.address_type_id || "";
    
    setEditedAddress({
      ...address,
      country_id: countryId,
      state_id: stateId,
      address_type_id: addressTypeId,
    });
  };

  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    setEditedAddress({
      ...editedAddress,
      [name]: value,
    });
  };

  const handleSaveAddress = async () => {
    if (!editedAddress.address_id) {
      console.error("Address ID is missing.");
      return;
    }

    const addressData = {
      address_id: editedAddress.address_id,
      company: company_id, 
      address_line1: editedAddress.address_line1,
      address_line2: editedAddress.address_line2,
      city: editedAddress.city,
      state: editedAddress.state_id, 
      country: editedAddress.country_id, 
      address_type: editedAddress.address_type_id, 
      zip_code: editedAddress.zip_code,
    };

    try {
      const response = await AxiosInstance.put(
        `companies/company/address/${editedAddress.address_id}/`,
        addressData
      );

      // If the address update is successful, update the local state
     const selectedCountry = countryOptions.find(
  (c) => String(c.country_id) === String(editedAddress.country_id)
);

const selectedState = stateOptions.find(
  (s) => String(s.state_id) === String(editedAddress.state_id)
);

const selectedAddressType = addressTypeOptions.find(
  (t) => String(t.address_type_id) === String(editedAddress.address_type_id)
);

const updatedAddresses = company.address.map((address) =>
  address.address_id === editedAddress.address_id
    ? {
        ...editedAddress,
        country: selectedCountry || address.country,
        state: selectedState || address.state,
        address_type: selectedAddressType || address.address_type,
      }
    : address
);

      setCompany({ ...company, address: updatedAddresses });
      setEditingAddress(null); // Close the modal
    } catch (error) {
      console.error("Error saving address:", error);
    }
  };

  const handleRemoveAddress = async (address_id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this address?"
    );

    if (!isConfirmed) {
      return;
    }

    try {
      await AxiosInstance.delete(
        `companies/company-address/${address_id}/`
      );
      const updatedAddresses = company.address.filter(
        (address) => address.address_id !== address_id
      );
      setCompany((prevState) => ({
        ...prevState,
        address: updatedAddresses,
      }));
    } catch (error) {
      console.error("Error removing address:", error);
    }
  };

  const userColumns = useMemo(
    () => [
      {
        Header: "Employee Id",
        accessor: "user_id",
        Cell: ({ row }) => (
          <span
            className="text-primary text-decoration-underline"
            style={{ cursor: "pointer" }}
            onClick={() => handleNavigateToUserProfile(row.original.user_id)}
          >
            {row.values.user_id}
          </span>
        ),
      },
      { Header: "FirstName", accessor: "first_name" },
      { Header: "LastName", accessor: "last_name" },
      { Header: "Email", accessor: "email" },
      { Header: "Mobile No", accessor: "mobile_no" },
      {
        Header: "Role",
        accessor: "roles",
        Cell: ({ row }) => {
          // Check if the user has roles
          const roles = row.values.roles;
          if (Array.isArray(roles) && roles.length > 0) {
            return roles.join(", ");
          }
          return "No roles"; // Fallback if no roles are found
        },
      },
      {
        Header: "Profile Names",
        accessor: "profiles",
        Cell: ({ row }) => {
          const profiles = row.values.profiles;
          if (Array.isArray(profiles) && profiles.length > 0) {
            return profiles.map((profile) => profile.profile_name).join(", ");
          }
          return "No profiles"; // Fallback if no profiles are found
        },
      },
      { Header: "Status", accessor: "status" },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleEditUser(row.original)}
            >
              <FiEdit />
            </button>
          </div>
        ),
      },
    ],
    [users]
  );

  const handleNavigateToUserProfile = (userId) => {
    const basePath = isInstitution ? "institution" : "company";
    navigate(`/${basePath}/user-profiles/${userId}`, {
      state: {
        from: location.pathname,
      },
    });
  };

  useEffect(() => {
    if (!company_id) {
      console.error("Company ID not found in URL");
      return;
    }
    const fetchCompanyUsers = async () => {
      try {
        // Fetch the list of users
        const usersResponse = await AxiosInstance.get(
          `companies/company-users/${company_id}/`
        );
        const usersData = usersResponse.data.company_users;

        // Fetch profiles for each user
        const usersWithProfiles = await Promise.all(
          usersData.map(async (user) => {
            try {
              const [profilesRes, userDetailsRes, rolesRes] = await Promise.all(
                [
                  AxiosInstance.get(
                    `companies/user/profile/${user.user_id}/`
                  ),
                  AxiosInstance.get(
                    `companies/user/details/${user.user_id}/`
                  ),
                  AxiosInstance.get(
                    `companies/user/roles/${user.user_id}/`
                  ),
                ]
              );

              const roles = rolesRes.data.map((roleObj) => roleObj.role); 

              return {
                ...userDetailsRes.data,
                profiles: profilesRes.data,
                roles: roles, 
              };
            } catch (error) {
              console.error(
                `Error fetching data for user ${user.user_id}:`,
                error
              );
              return {
                ...user,
                profiles: [],
                roles: [],
              };
            }
          })
        );
        setUsers(usersWithProfiles); 
      } catch (error) {
        console.error("Error fetching company users:", error);
      }
    };
    fetchCompanyUsers();
  }, [company_id]);

  // Function to handle the edit button click
  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditedUser({ ...user });
  };

const handleSaveUser = async () => {
  try {
    const formData = new FormData();

    // Append only the necessary fields
    formData.append("first_name", editedUser.first_name || "");
    formData.append("last_name", editedUser.last_name || "");
    formData.append("email", editedUser.email || "");
    formData.append("mobile_no", editedUser.mobile_no || "");
    formData.append("status", editedUser.status || "Active");

    await AxiosInstance.put(
      `companies/user/update/${editedUser.user_id}/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Update local state
    const updatedUsers = users.map((user) =>
      user.user_id === editedUser.user_id ? { ...user, ...editedUser } : user
    );
    setUsers(updatedUsers);
    setEditingUser(null);
  } catch (error) {
    console.error("Error saving user:", error);
  }
};

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser({
      ...editedUser,
      [name]: value,
    });
  };

  const handleUserSearchChange = (e) => {
    setSearchUserTerm(e.target.value);
  };

  const filteredUsers = useMemo(() => {
    if (!searchUserTerm) return users;

    const term = searchUserTerm.toLowerCase();

    return users.filter(
      (user) =>
        (user.user_id && String(user.user_id).toLowerCase().includes(term)) ||
        (user.first_name && user.first_name.toLowerCase().includes(term)) ||
        (user.last_name && user.last_name.toLowerCase().includes(term)) ||
        (user.email && user.email.toLowerCase().includes(term)) ||
        (user.mobile_no &&
          String(user.mobile_no).toLowerCase().includes(term)) ||
        (Array.isArray(user.roles) &&
          user.roles.join(", ").toLowerCase().includes(term)) ||
        (Array.isArray(user.profiles) &&
          user.profiles
            .map((profile) => profile.profile_name || "")
            .join(", ")
            .toLowerCase()
            .includes(term))
    );
  }, [searchUserTerm, users]);

  // Pass filteredUsers to the table instance
  const userTableInstance = useTable(
    {
      columns: userColumns,
      data: filteredUsers,
      initialState: { globalFilter: userFilter, pageSize: 5 },
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  const {
    getTableProps: getUserTableProps,
    getTableBodyProps: getUserTableBodyProps,
    headerGroups: userHeaderGroups,
    prepareRow: prepareUserRow,
    page: userPage,
    state: { pageIndex: userPageIndex },
    gotoPage: gotoUserPage,
    pageCount: userPageCount,
    nextPage: nextUserPage,
    previousPage: previousUserPage,
    canNextPage: canNextUserPage,
    canPreviousPage: canPreviousUserPage,
    setPageSize: setUserPageSize,
    setGlobalFilter: setUserGlobalFilter,
  } = userTableInstance;

  const handleBack = () => {
    const from = location.state?.from;
    if (from) {
      navigate(from);
      return;
    }
    navigate(isInstitution ? "/institution-details" : "/company-details");
  };

  return (
    <div className="CompanyPage als-employer-page">
      <CompanyHeader />
      <div className="company-container py-4">
        <div className="content">
          <div className="mb-3">
            <button
              className="navigate-button btn btn-outline-primary d-flex align-items-center"
              onClick={handleBack}
              style={{ gap: "6px" }}
            >
              <FiArrowLeft /> Back
            </button>
          </div>
          {/* Search Address Field and Add Button */}
          <h3 className="section-heading">Addresses:</h3>
          <div className="card-custom">
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                placeholder="Search Address"
                value={searchTerm}
                onChange={handleSearchChange}
                className="form-control w-50"
              />
              <button
                className="add-button"
                onClick={() =>
                  navigate(`/add-address`, {
                    state: { from: location.pathname },
                  })
                }
              >
                Add
              </button>
            </div>
            <div className="table-responsive">
              <table
                className="table table-bordered table-striped"
                {...getTableProps()}
              >
                <thead className="table-light">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.length === 0 ? (
                    <tr>
                      <td colSpan={headerGroups[0].headers.length}>
                        No address found
                      </td>
                    </tr>
                  ) : (
                    page.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="pagination d-flex justify-content-center align-items-center gap-3 mt-3">
                <button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  className="btn btn-outline-primary"
                >
                  Previous
                </button>
                <span>
                  Page{" "}
                  <strong>
                    {pageIndex + 1} of {pageOptions.length || 1}
                  </strong>{" "}
                </span>
                <button
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  className="btn btn-outline-primary"
                >
                  Next
                </button>
              </div>

            {editingAddress && (
              <div
                className="modal fade show d-block"
                tabIndex="-1"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
              >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Edit Address</h5>
                    </div>
                    <div className="modal-body">
                      <form>
                        <div className="mb-3 position-relative">
                          <label className="form-label">Search Address</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type address..."
                            value={editAddressSearch}
                            onChange={(e) =>
                              setEditAddressSearch(e.target.value)
                            }
                          />
                          {editSuggestions.length > 0 && (
                            <ul
                              className="list-group position-absolute w-100"
                              style={{ zIndex: 999 }}
                            >
                              {editSuggestions.map((item, index) => (
                                <li
                                  key={index}
                                  className="list-group-item list-group-item-action"
                                  onClick={() =>
                                    handleEditAddressSelect(item.place_id)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  {item.description}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                          <label className="form-label">Address Line 1</label>
                          <input
                            type="text"
                            className="form-control"
                            name="address_line1"
                            value={editedAddress.address_line1}
                            onChange={handleAddressInputChange}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Address Line 2</label>
                          <input
                            type="text"
                            className="form-control"
                            name="address_line2"
                            value={editedAddress.address_line2}
                            onChange={handleAddressInputChange}
                          />
                        </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                          <label className="form-label">City</label>
                          <input
                            type="text"
                            className="form-control"
                            name="city"
                            value={editedAddress.city}
                            onChange={handleAddressInputChange}
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Zip Code</label>
                          <input
                            type="text"
                            className="form-control"
                            name="zip_code"
                            value={editedAddress.zip_code}
                            onChange={handleAddressInputChange}
                          />
                        </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Country</label>
                            <select
                              className="form-select"
                              value={editedAddress.country_id ? String(editedAddress.country_id) : ""}
                              onChange={(e) =>
                                setEditedAddress({
                                  ...editedAddress,
                                  country_id: e.target.value,
                                  state_id: "", // Reset state when country changes
                                })
                              }
                            >
                              <option value="">Select Country</option>
                              {countryOptions.map((c) => (
                                <option key={c.country_id} value={String(c.country_id)}>
                                  {c.country_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">State</label>
                            <select
                              className="form-select"
                              value={editedAddress.state_id ? String(editedAddress.state_id) : ""}
                              disabled={!editedAddress.country_id}
                              onChange={(e) =>
                                setEditedAddress({
                                  ...editedAddress,
                                  state_id: e.target.value,
                                })
                              }
                            >
                              <option value="">Select State</option>
                              {stateOptions.map((s) => (
                                <option key={s.state_id} value={String(s.state_id)}>
                                  {s.state_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Address Type</label>
                          <select
                            className="form-select"
                            value={editedAddress.address_type_id || ""}
                            onChange={(e) =>
                              setEditedAddress({
                                ...editedAddress,
                                address_type_id: e.target.value,
                              })
                            }
                          >
                            <option value="">Select Address Type</option>
                            {addressTypeOptions.map((type) => (
                              <option
                                key={type.address_type_id}
                                value={type.address_type_id}
                              >
                                {type.address_type}
                              </option>
                            ))}
                          </select>
                        </div>
                      </form>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setEditingAddress(null)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveAddress}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* User Table */}
          <h3 className="section-heading">Employees:</h3>
          <div className="card-custom">
            {/* Search Bar for Users */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                placeholder="Search Employee"
                value={searchUserTerm}
                onChange={handleUserSearchChange}
                className="form-control w-50"
              />
              <button
                className="add-button"
                onClick={() =>
                  navigate(`/add-user`, {
                    state: { from: location.pathname },
                  })
                }
              >
                Add
              </button>
            </div>

            <div className="table-responsive">
              <table
                className="table table-bordered table-striped"
                {...getUserTableProps()}
              >
                <thead className="table-light">
                  {userHeaderGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getUserTableBodyProps()}>
                  {userPage.length === 0 ? (
                    <tr>
                      <td colSpan={userHeaderGroups[0].headers.length}>
                        No users found
                      </td>
                    </tr>
                  ) : (
                    userPage.map((row) => {
                      prepareUserRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

              <div className="pagination d-flex justify-content-center align-items-center gap-3 mt-3">
                <button
                  onClick={() => previousUserPage()}
                  disabled={!canPreviousUserPage}
                  className="btn btn-sm btn-outline-primary"
                >
                  Previous
                </button>
                <span>
                  Page{" "}
                  <strong>
                    {userPageIndex + 1} of {userPageCount || 1}
                  </strong>
                </span>
                <button
                  onClick={() => nextUserPage()}
                  disabled={!canNextUserPage}
                  className="btn btn-sm btn-outline-primary"
                >
                  Next
                </button>
              </div>
          </div>
          {editingUser && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-md modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit User</h5>
                  </div>
                  <div className="modal-body">
                    <form>
                      <div className="mb-3">
                        <label className="form-label">First Name</label>
                        <input
                          type="text"
                          name="first_name"
                          className="form-control"
                          value={editedUser.first_name}
                          onChange={handleUserInputChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Last Name</label>
                        <input
                          type="text"
                          name="last_name"
                          className="form-control"
                          value={editedUser.last_name}
                          onChange={handleUserInputChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          value={editedUser.email}
                          onChange={handleUserInputChange}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Mobile No</label>
                        <input
                          type="text"
                          name="mobile_no"
                          className="form-control"
                          value={editedUser.mobile_no}
                          onChange={handleUserInputChange}
                        />
                      </div>
                      <div className="mb-3">
  <label className="form-label">Status</label>
  <select
    name="status"
    className="form-control"
    value={editedUser.status}
    onChange={handleUserInputChange}
  >
    <option value="">-- Select Status --</option>
    <option value="Active">Active</option>
    <option value="Inactive">Inactive</option>
  </select>
</div>

                    </form>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingUser(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveUser}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
